import {
  QueryFunction,
  QueryKey,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

import { coreLoginClient } from 'app/api/apiClient/generated/coreLoginClient';
import { CoreApiError } from 'app/api/hooks/core/v1/errors';
import { useAuthDispatch } from 'app/state/AuthContext/hooks';

export function useQueryEnhanced<TResult extends AxiosResponse, TError = unknown>(
  queryKey: QueryKey,
  queryFn: QueryFunction<TResult>,
  options: UseQueryOptions<TResult['data'], TError, TResult['data']> = {},
): UseQueryResult<TResult['data'], TError> {
  const dispatch = useAuthDispatch();

  const query = useCallback(async () => {
    try {
      const { data } = await queryFn.apply(null) as AxiosResponse<TResult>;

      return data;
    } catch (error) {
      if ((error as CoreApiError<TError>).status === 401) {
        try {
          const { data } = await coreLoginClient.newToken();
          dispatch({
            payload: {
              token: data.token,
            },
            type: 'setLoginInfo',
          });
        } catch {
          window.location.reload();
        }
      } else {
        throw error;
      }
    }
  },
  [dispatch, queryFn]);

  return useQuery(
    queryKey,
    query,
    options,
  );
}
